/**
 * PlanSheet — unified chat-first Smart Plan builder.
 *
 * Design inspired by Claude's chat UI:
 *  - Chat input at top (always visible)
 *  - Template chips below input (shown when no activity yet)
 *  - Typing text → AI generates plan conversationally
 *  - Clicking template → pack flow (questions → date → execute)
 *
 * Single sheet replaces the old separate PlanSheet + PlanChatSheet.
 */

import {
  memo,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  forwardRef,
  type ForwardedRef,
} from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/shared/utils/general/cn"
import type { DbObject } from "@/db/schema"
import { executePlan, BUILT_IN_PACKS } from "@/db/operations"
import { evaluateCondition } from "@totodo/operations"
import { toUtcMidnight } from "@/shared/utils/date/dateUtils"
import type { DbPack, DbSkill, PackQuestion, AiChatMessage } from "@totodo/operations"
import { ResponsiveSheet } from "@/app/components/ui/responsive-sheet"
import { ContainerIcon } from "@/shared/icons/resolveContainerIcon"
import { MiniCalendar } from "@/features/schedule/MiniCalendar"
import { PlanPreview } from "./PlanPreview"
import { useAiPlanChat } from "./useAiPlanChat"
import CrossIcon from "@/shared/icons/totodoicons/stroke/cross.svg?react"
import ChevronLeftIcon from "@/shared/icons/totodoicons/stroke/chevron-left.svg?react"
import SendIcon from "@/shared/icons/totodoicons/stroke/arrow-up.svg?react"
import { CircularProgress } from "@/app/components/ui/CircularProgress"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlanSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  task: DbObject
  /** Pre-selected skill to guide AI generation. */
  skill?: DbSkill
}

type Step = "chat" | "questions" | "date" | "executing" | "save"

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PlanSheet = memo(function PlanSheet({
  open,
  onOpenChange,
  task,
  skill,
}: PlanSheetProps) {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>("chat")
  const [selectedPack, setSelectedPack] = useState<DbPack | null>(null)
  const [answers, setAnswers] = useState<Record<string, unknown>>({})
  const [anchorDate, setAnchorDate] = useState<Date>(new Date())
  const [executing, setExecuting] = useState(false)
  const [result, setResult] = useState<{
    ok: boolean
    taskCount?: number
    error?: string
  } | null>(null)
  const [saveMode, setSaveMode] = useState<"skill" | "pack" | null>(null)
  const [saveName, setSaveName] = useState("")
  const [saveCategory, setSaveCategory] = useState("")
  const [saved, setSaved] = useState(false)

  // AI chat state
  const aiChat = useAiPlanChat()
  const {
    messages,
    proposal,
    isGenerating,
    error: chatError,
    sendMessage,
    acceptPlan,
    saveAsSkill,
    saveAsPack,
  } = aiChat

  const chatEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Scroll chat to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, isGenerating])

  // Reset state when sheet closes
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setTimeout(() => {
          setStep("chat")
          setSelectedPack(null)
          setAnswers({})
          setAnchorDate(new Date())
          setExecuting(false)
          setResult(null)
          setSaveMode(null)
          setSaveName("")
          setSaveCategory("")
          setSaved(false)
        }, 300)
      }
      onOpenChange(open)
    },
    [onOpenChange],
  )

  const handleClose = useCallback(
    () => handleOpenChange(false),
    [handleOpenChange],
  )

  // --- AI chat path ---
  const handleSend = useCallback(
    (text: string) => {
      if (!text.trim() || isGenerating) return
      sendMessage(text.trim(), skill)
    },
    [sendMessage, isGenerating, skill],
  )

  const handleAcceptProposal = useCallback(() => {
    setStep("date")
  }, [])

  // --- Template/Pack path ---
  const handleSelectPack = useCallback((pack: DbPack) => {
    setSelectedPack(pack)
    setAnswers(
      Object.fromEntries(
        (pack.questions ?? [])
          .filter((q) => q.default !== undefined)
          .map((q) => [q.key, q.default]),
      ),
    )
    if (!pack.questions?.length) {
      setStep("date")
    } else {
      setStep("questions")
    }
  }, [])

  const handleAnswerChange = useCallback(
    (key: string, value: unknown) => {
      setAnswers((prev) => ({ ...prev, [key]: value }))
    },
    [],
  )

  const handleQuestionsNext = useCallback(() => {
    setStep("date")
  }, [])

  // --- Execute (both paths converge here) ---
  const handleExecute = useCallback(async () => {
    if (!task.id) return
    setExecuting(true)
    setStep("executing")

    if (selectedPack) {
      // Pack path
      const res = await executePlan({
        anchorTaskId: task.id,
        anchorDate: toUtcMidnight(anchorDate),
        anchorDateType: selectedPack.anchorDateType,
        pack: selectedPack,
        answers,
      })
      if (res.ok) {
        setResult({ ok: true, taskCount: res.data.taskIds.length })
      } else {
        setResult({ ok: false, error: res.error })
      }
    } else if (proposal) {
      // AI path
      const res = await acceptPlan(task.id, toUtcMidnight(anchorDate))
      if (res.ok) {
        setResult({ ok: true, taskCount: res.data.taskIds.length })
      } else {
        setResult({ ok: false, error: res.error })
      }
    }
    setExecuting(false)
  }, [selectedPack, proposal, task.id, anchorDate, answers, acceptPlan])

  // --- Save ---
  const handleSave = useCallback(async () => {
    if (!saveName.trim()) return
    const category = saveCategory.trim() || "general"
    if (saveMode === "skill") {
      await saveAsSkill(saveName.trim(), category)
    } else {
      await saveAsPack(saveName.trim(), category)
    }
    setSaved(true)
  }, [saveMode, saveName, saveCategory, saveAsSkill, saveAsPack])

  // --- Navigation ---
  const handleBack = useCallback(() => {
    if (step === "questions") setStep("chat")
    else if (step === "date") {
      if (selectedPack?.questions?.length) setStep("questions")
      else setStep("chat")
    } else if (step === "save") setStep("executing")
  }, [step, selectedPack])

  // Step count (for pack path)
  const activeStepCount = useMemo(() => {
    if (proposal) return proposal.steps.length
    if (!selectedPack) return 0
    return selectedPack.steps.filter((s) =>
      evaluateCondition(s.condition, answers),
    ).length
  }, [selectedPack, proposal, answers])

  // Visible questions (pack path)
  const visibleQuestions = useMemo(() => {
    if (!selectedPack?.questions) return []
    return selectedPack.questions.filter((q) =>
      evaluateCondition(q.condition, answers),
    )
  }, [selectedPack, answers])

  // Is initial state (no messages, no pack selected)
  const isInitial = messages.length === 0 && !isGenerating && step === "chat"

  return (
    <ResponsiveSheet
      open={open}
      onOpenChange={handleOpenChange}
      title={t("Smart Plan")}
      className="rounded-t-2xl"
    >
      <div className="flex max-h-[85vh] flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2 border-b px-4 py-3">
          {step !== "chat" && step !== "executing" && (
            <button
              onClick={handleBack}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent"
            >
              <ChevronLeftIcon className="size-4" />
            </button>
          )}
          <h3 className="flex-1 text-sm font-semibold">
            {step === "chat" && t("Smart Plan")}
            {step === "questions" && selectedPack?.name}
            {step === "date" && t("When?")}
            {step === "executing" && t("Creating plan...")}
            {step === "save" && t("Save as template")}
          </h3>
          {step === "chat" && messages.length > 0 && (
            <button
              onClick={aiChat.reset}
              className="rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent"
            >
              {t("New")}
            </button>
          )}
          <button
            onClick={handleClose}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent"
          >
            <CrossIcon className="size-4" />
          </button>
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto">
          {step === "chat" && !isInitial && (
            <div className="flex flex-col gap-2 px-4 py-3">
              {/* Messages */}
              {messages.map((msg, i) => (
                <ChatBubble
                  key={i}
                  message={msg}
                  showAccept={
                    msg.role === "assistant" &&
                    !!msg.proposal &&
                    i === messages.length - 1
                  }
                  onAccept={handleAcceptProposal}
                />
              ))}

              {/* Generating indicator */}
              {isGenerating && (
                <div className="flex items-center gap-2 py-2">
                  <div className="flex gap-1">
                    <span className="size-1.5 animate-bounce rounded-full bg-primary [animation-delay:-0.3s]" />
                    <span className="size-1.5 animate-bounce rounded-full bg-primary [animation-delay:-0.15s]" />
                    <span className="size-1.5 animate-bounce rounded-full bg-primary" />
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {t("Generating plan...")}
                  </span>
                </div>
              )}

              {/* Error */}
              {chatError && (
                <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  {chatError}
                </div>
              )}

              <div ref={chatEndRef} />
            </div>
          )}

          {step === "questions" && (
            <div className="px-4 py-3">
              <QuestionForm
                questions={visibleQuestions}
                answers={answers}
                onChange={handleAnswerChange}
              />
            </div>
          )}

          {step === "date" && (
            <div className="flex flex-col gap-3 px-4 py-3">
              <p className="text-sm text-muted-foreground">
                {t("Pick the anchor date for this plan")}
              </p>
              <MiniCalendar
                selectedDate={anchorDate}
                onSelect={(d: Date) => setAnchorDate(d)}
              />
              {proposal && (
                <div className="rounded-md bg-accent/40 px-3 py-2">
                  <PlanPreview proposal={proposal} compact />
                </div>
              )}
              {!proposal && (
                <div className="rounded-md bg-accent/40 px-3 py-2 text-sm">
                  <span className="font-medium">{activeStepCount}</span>{" "}
                  {t("tasks will be created")}
                </div>
              )}
            </div>
          )}

          {step === "executing" && (
            <ExecutingView
              executing={executing}
              result={result}
              onClose={handleClose}
              onRetry={() => setStep("date")}
              onSave={() => {
                setSaveName(proposal?.name ?? selectedPack?.name ?? "")
                setStep("save")
              }}
              showSave={!!proposal}
            />
          )}

          {step === "save" && (
            <SaveView
              saveMode={saveMode}
              setSaveMode={setSaveMode}
              saveName={saveName}
              setSaveName={setSaveName}
              saveCategory={saveCategory}
              setSaveCategory={setSaveCategory}
              saved={saved}
              onSave={handleSave}
              onClose={handleClose}
            />
          )}
        </div>

        {/* Footer — chat input + template chips, or action buttons */}
        {step === "chat" && (
          <div className="border-t">
            <ChatInput
              ref={inputRef}
              onSend={handleSend}
              disabled={isGenerating}
              hasProposal={!!proposal}
            />
            {/* Template chips — shown only in initial state */}
            {isInitial && (
              <div className="px-4 pb-3">
                <div className="flex flex-wrap gap-1.5">
                  {BUILT_IN_PACKS.map((pack) => (
                    <button
                      key={pack.id}
                      onClick={() => handleSelectPack(pack)}
                      className={cn(
                        "flex items-center gap-1.5 rounded-full border px-3 py-1.5",
                        "text-xs font-medium transition-colors hover:bg-accent",
                      )}
                    >
                      <ContainerIcon icon={pack.icon} size="0.875rem" />
                      <span>{pack.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {step === "questions" && (
          <div className="border-t px-4 py-3">
            <button
              onClick={handleQuestionsNext}
              className={cn(
                "w-full rounded-md px-4 py-2 text-sm font-medium",
                "bg-primary text-primary-foreground transition-colors hover:bg-primary/90",
              )}
            >
              {t("Next")}
            </button>
          </div>
        )}

        {step === "date" && (
          <div className="border-t px-4 py-3">
            <button
              onClick={handleExecute}
              className={cn(
                "w-full rounded-md px-4 py-2 text-sm font-medium",
                "bg-primary text-primary-foreground transition-colors hover:bg-primary/90",
              )}
            >
              {t("Create plan")} — {activeStepCount} {t("tasks")}
            </button>
          </div>
        )}
      </div>
    </ResponsiveSheet>
  )
})

// ---------------------------------------------------------------------------
// ChatBubble — single message with optional plan preview
// ---------------------------------------------------------------------------

function ChatBubble({
  message,
  showAccept,
  onAccept,
}: {
  message: AiChatMessage
  showAccept: boolean
  onAccept: () => void
}) {
  const { t } = useTranslation()
  const isUser = message.role === "user"

  return (
    <div
      className={cn(
        "flex flex-col gap-1.5",
        isUser ? "items-end" : "items-start",
      )}
    >
      <div
        className={cn(
          "max-w-[85%] rounded-xl px-3 py-2 text-sm",
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-accent text-foreground",
        )}
      >
        {message.content}
      </div>

      {/* Inline plan preview */}
      {message.proposal && (
        <div className="w-full max-w-[95%] rounded-xl border bg-background p-3">
          <PlanPreview proposal={message.proposal} />

          {/* Suggested questions */}
          {message.proposal.suggestedQuestions &&
            message.proposal.suggestedQuestions.length > 0 && (
              <div className="mt-2 border-t pt-2">
                <p className="text-xs font-medium text-muted-foreground">
                  {t("I could also use more context:")}
                </p>
                <ul className="mt-1 flex flex-col gap-0.5">
                  {message.proposal.suggestedQuestions.map((q: string, i: number) => (
                    <li key={i} className="text-xs text-muted-foreground">
                      • {q}
                    </li>
                  ))}
                </ul>
              </div>
            )}

          {/* Accept button */}
          {showAccept && (
            <button
              onClick={onAccept}
              className={cn(
                "mt-3 w-full rounded-md px-4 py-2 text-sm font-medium",
                "bg-primary text-primary-foreground transition-colors hover:bg-primary/90",
              )}
            >
              {t("Accept plan")} →
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ChatInput — text area with send button
// ---------------------------------------------------------------------------

const ChatInput = forwardRef(function ChatInput(
  {
    onSend,
    disabled,
    hasProposal,
  }: {
    onSend: (text: string) => void
    disabled: boolean
    hasProposal: boolean
  },
  ref: ForwardedRef<HTMLTextAreaElement>,
) {
  const { t } = useTranslation()
  const [text, setText] = useState("")

  const handleSend = useCallback(() => {
    if (!text.trim() || disabled) return
    onSend(text.trim())
    setText("")
  }, [text, disabled, onSend])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  return (
    <div className="px-4 py-3">
      <div className="flex items-end gap-2">
        <textarea
          ref={ref}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            hasProposal
              ? t("Refine the plan...")
              : t("Describe what you want to plan...")
          }
          disabled={disabled}
          rows={1}
          className={cn(
            "max-h-24 min-h-[36px] flex-1 resize-none rounded-lg border bg-background px-3 py-2 text-sm",
            "outline-none focus:ring-1 focus:ring-primary",
            "disabled:opacity-50",
          )}
        />
        <button
          onClick={handleSend}
          disabled={!text.trim() || disabled}
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-lg",
            "bg-primary text-primary-foreground transition-colors",
            "disabled:opacity-30",
          )}
        >
          <SendIcon className="size-4" />
        </button>
      </div>
    </div>
  )
})

// ---------------------------------------------------------------------------
// QuestionForm — dynamic form based on pack.questions
// ---------------------------------------------------------------------------

function QuestionForm({
  questions,
  answers,
  onChange,
}: {
  questions: PackQuestion[]
  answers: Record<string, unknown>
  onChange: (key: string, value: unknown) => void
}) {
  return (
    <div className="flex flex-col gap-4">
      {questions.map((q) => (
        <QuestionField
          key={q.key}
          question={q}
          value={answers[q.key]}
          onChange={(val) => onChange(q.key, val)}
        />
      ))}
    </div>
  )
}

function QuestionField({
  question,
  value,
  onChange,
}: {
  question: PackQuestion
  value: unknown
  onChange: (value: unknown) => void
}) {
  if (question.type === "boolean") {
    return (
      <label className="flex items-center gap-3 text-sm">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="size-4 rounded border-border accent-primary"
        />
        <span>{question.label}</span>
      </label>
    )
  }

  if (question.type === "select" && question.options) {
    return (
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium">{question.label}</label>
        <div className="flex flex-col gap-1">
          {question.options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={cn(
                "rounded-md px-3 py-2 text-left text-sm transition-colors",
                value === opt.value
                  ? "bg-primary/10 font-medium text-primary"
                  : "hover:bg-accent",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    )
  }

  if (question.type === "number") {
    return (
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium">{question.label}</label>
        <input
          type="number"
          value={value != null ? String(value) : ""}
          onChange={(e) => onChange(Number(e.target.value))}
          className="rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
        />
      </div>
    )
  }

  // Default: text
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium">{question.label}</label>
      <input
        type="text"
        value={value != null ? String(value) : ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={question.label}
        className="rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
        autoFocus={question.required}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// ExecutingView — plan creation progress/result
// ---------------------------------------------------------------------------

function ExecutingView({
  executing,
  result,
  onClose,
  onRetry,
  onSave,
  showSave,
}: {
  executing: boolean
  result: { ok: boolean; taskCount?: number; error?: string } | null
  onClose: () => void
  onRetry: () => void
  onSave: () => void
  showSave: boolean
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col items-center gap-4 px-4 py-8">
      {executing && (
        <>
          <CircularProgress className="size-8" />
          <p className="text-sm text-muted-foreground">
            {t("Creating tasks...")}
          </p>
        </>
      )}
      {result?.ok && (
        <>
          <div className="text-4xl">✅</div>
          <p className="text-sm font-medium">
            {t("Created {{count}} tasks!", { count: result.taskCount })}
          </p>
          <div className="flex w-full flex-col gap-2">
            {showSave && (
              <button
                onClick={onSave}
                className={cn(
                  "w-full rounded-md border px-4 py-2 text-sm font-medium",
                  "transition-colors hover:bg-accent",
                )}
              >
                {t("Save as reusable template")}
              </button>
            )}
            <button
              onClick={onClose}
              className={cn(
                "w-full rounded-md px-4 py-2 text-sm font-medium",
                "bg-primary text-primary-foreground transition-colors hover:bg-primary/90",
              )}
            >
              {t("Done")}
            </button>
          </div>
        </>
      )}
      {result && !result.ok && (
        <>
          <div className="text-4xl">❌</div>
          <p className="text-sm text-destructive">{result.error}</p>
          <button
            onClick={onRetry}
            className="rounded-md px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-accent"
          >
            {t("Try again")}
          </button>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// SaveView — save as Skill or Pack
// ---------------------------------------------------------------------------

function SaveView({
  saveMode,
  setSaveMode,
  saveName,
  setSaveName,
  saveCategory,
  setSaveCategory,
  saved,
  onSave,
  onClose,
}: {
  saveMode: "skill" | "pack" | null
  setSaveMode: (mode: "skill" | "pack") => void
  saveName: string
  setSaveName: (name: string) => void
  saveCategory: string
  setSaveCategory: (category: string) => void
  saved: boolean
  onSave: () => void
  onClose: () => void
}) {
  const { t } = useTranslation()

  if (saved) {
    return (
      <div className="flex flex-col items-center gap-4 px-4 py-8">
        <div className="text-4xl">💾</div>
        <p className="text-sm font-medium">{t("Template saved!")}</p>
        <p className="text-center text-xs text-muted-foreground">
          {saveMode === "skill"
            ? t(
                "Saved as a Skill — AI will use this as a guide for future plan generation.",
              )
            : t(
                "Saved as a Pack — exact steps will be replicated each time.",
              )}
        </p>
        <button
          onClick={onClose}
          className={cn(
            "w-full rounded-md px-4 py-2 text-sm font-medium",
            "bg-primary text-primary-foreground transition-colors hover:bg-primary/90",
          )}
        >
          {t("Done")}
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 px-4 py-3">
      {/* Type picker */}
      {!saveMode && (
        <>
          <p className="text-sm font-medium">{t("How should this be saved?")}</p>
          <button
            onClick={() => setSaveMode("skill")}
            className={cn(
              "flex flex-col items-start gap-1 rounded-lg border p-3 text-left",
              "transition-colors hover:bg-accent",
            )}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">🧠</span>
              <span className="text-sm font-medium">{t("As a Skill")}</span>
              <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                {t("Recommended")}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {t(
                "AI uses this as a guide — each plan adapts to context. Shareable as a template others can customize.",
              )}
            </p>
          </button>
          <button
            onClick={() => setSaveMode("pack")}
            className={cn(
              "flex flex-col items-start gap-1 rounded-lg border p-3 text-left",
              "transition-colors hover:bg-accent",
            )}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">📋</span>
              <span className="text-sm font-medium">{t("As a Pack")}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {t(
                "Exact steps are saved — same tasks every time. Good for recurring processes.",
              )}
            </p>
          </button>
        </>
      )}

      {/* Name + category form */}
      {saveMode && (
        <>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">{t("Name")}</label>
            <input
              type="text"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder={t("e.g. Birthday Party Planner")}
              className="rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">{t("Category")}</label>
            <input
              type="text"
              value={saveCategory}
              onChange={(e) => setSaveCategory(e.target.value)}
              placeholder={t("e.g. events, travel, work")}
              className="rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <button
            onClick={onSave}
            disabled={!saveName.trim()}
            className={cn(
              "w-full rounded-md px-4 py-2 text-sm font-medium",
              "bg-primary text-primary-foreground transition-colors hover:bg-primary/90",
              "disabled:opacity-50",
            )}
          >
            {saveMode === "skill"
              ? t("Save as Skill")
              : t("Save as Pack")}
          </button>
        </>
      )}
    </div>
  )
}
